import { wrapSubtitleTextByTokens } from './autoSubtitleGeometry';

/** 后端下发的断行标记（前导空格 + \N） */
export const SUBTITLE_BREAK_MARKER_BACKEND = ' \N';
/** 前端写入的断行标记（前导空格 + \\N） */
export const SUBTITLE_BREAK_MARKER_FRONTEND = ' \\N';

/** 匹配 ` \N` 或 ` \\N`（均含前导空格，展示时一并隐藏） */
export const SUBTITLE_LINE_BREAK_MARKER_RE = / \\N| \\\\N/g;

/**
 * 右侧目标语编辑区：用户手打的换行符一律剔除（断点仅由视频字幕框驱动）。
 */
export function stripTargetTextUserLineBreaks(text: string): string {
  return text.replace(/\r\n|\n|\r/g, '');
}

/** 剔除手打换行后是否与基准文案相同（用于跳过无意义保存/API） */
export function isTargetTextUnchangedAfterStrippingUserBreaks(
  nextText: string,
  baselineText: string
): boolean {
  return (
    stripTargetTextUserLineBreaks(nextText) === stripTargetTextUserLineBreaks(baselineText)
  );
}

export function hasSubtitleLineBreakMarkers(text: string): boolean {
  return SUBTITLE_LINE_BREAK_MARKER_RE.test(text);
}

/** 去掉全部 ` \N`、` \\N`（含标记前空格），得到纯文本 */
export function stripSubtitleLineBreakMarkers(text: string): string {
  return text.replace(SUBTITLE_LINE_BREAK_MARKER_RE, '');
}

/** ②③ 列表/时间轴：单行展示，不解析为换行 */
export function formatSubtitlePostTextForListDisplay(text: string): string {
  return stripSubtitleLineBreakMarkers(stripTargetTextUserLineBreaks(text));
}

/** ① 叠加层：按标记拆成多行（无标记时整段为一行，再由 wrap 处理） */
export function parseSubtitleOverlayLines(text: string): string[] {
  const trimmed = stripTargetTextUserLineBreaks(text).trim();
  if (!trimmed) return [];
  if (!hasSubtitleLineBreakMarkers(trimmed)) {
    return [trimmed];
  }
  return trimmed
    .split(SUBTITLE_LINE_BREAK_MARKER_RE)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** 多行 wrap 结果 → 带前端 ` \\N` 的 postText；单行不写标记 */
export function buildPostTextFromWrapLines(lines: string[]): string {
  const nonEmpty = lines.map((l) => l.trim()).filter(Boolean);
  if (nonEmpty.length === 0) return '';
  if (nonEmpty.length === 1) return nonEmpty[0];
  return nonEmpty.join(`${SUBTITLE_BREAK_MARKER_FRONTEND} `);
}

/**
 * 先清标记，再按当前框宽 wrap，生成待提交的 postText（前端 ` \\N`）。
 */
export function buildPostTextFromPlainAndWrap(
  rawPostText: string,
  maxTextWidthPx: number,
  measureTextWidth: (line: string) => number
): string {
  const plain = stripSubtitleLineBreakMarkers(stripTargetTextUserLineBreaks(rawPostText)).trim();
  if (!plain) return '';
  if (maxTextWidthPx <= 0) return plain;
  const lines = wrapSubtitleTextByTokens(plain, maxTextWidthPx, measureTextWidth);
  return buildPostTextFromWrapLines(lines);
}

/** 保存/比对用：去掉用户换行与断行标记 */
export function normalizeTargetPostTextFromEditor(text: string): string {
  return stripSubtitleLineBreakMarkers(stripTargetTextUserLineBreaks(text)).trim();
}

export function isPostTextEqualIgnoringMarkers(a: string, b: string): boolean {
  return normalizeTargetPostTextFromEditor(a) === normalizeTargetPostTextFromEditor(b);
}

/** PUT /video/subtitles/batch-update-post-text 请求体 */
export interface BatchUpdatePostTextRequestPayload {
  taskId: number;
  subtitles: Array<{ id: number; postText: string }>;
}

export function buildBatchUpdatePostTextRequestPayload(
  taskId: number,
  subtitles: Array<{ id: number; postText: string }>
): BatchUpdatePostTextRequestPayload {
  return { taskId, subtitles };
}
